/* ============================================================================
   TANGIG — identité de marque
   ----------------------------------------------------------------------------
   Trois composants présentationnels, sans état :

     <TangigMark />      le symbole seul — trois entailles à l'angle du A
     <TangigWordmark />  le mot TANGIG, dont le A est ouvert (sans traverse)
     <TangigLockup />    symbole + mot, alignés sur la même ligne de base

   Toute la géométrie est exprimée dans une grille dont l'unité est la hauteur
   de capitale = 100. Elle est DÉFINITIVE : ne pas « arrondir » les valeurs.

   ── Révision : les entailles dérivent désormais du A ──────────────────────
   Le symbole était tracé à 70° pendant que les jambes du A tombent à 68,45°.
   1,55° d'écart : assez pour que l'œil lise deux objets étrangers, et le
   symbole passait pour un ornement posé devant le mot. Les entailles reprennent
   donc EXACTEMENT l'angle du A — ratio dx/dy = 0.395, soit la pente de sa
   jambe gauche (0,100)→(39.5,0). Le symbole n'est plus décoratif : c'est la
   même construction que la lettre, répétée trois fois.

   Trois conséquences, toutes voulues :

   - Les entailles sont des POLYGONES PLEINS, plus des traits. Leurs extrémités
     sont coupées à l'HORIZONTALE, exactement comme le haut et le bas des jambes
     du A — et non perpendiculairement à la pente comme le ferait n'importe quel
     `stroke`. C'est ce détail qui rend la géométrie propriétaire.
   - L'épaisseur est 13,2 perpendiculaire (14,2 mesuré à l'horizontale), contre
     12 auparavant. Elle est calée ~13 % au-dessus des jambes du A (11,6 à cette
     graisse) : le symbole est presque toujours reproduit seul et plus petit
     (favicon, icône, panneau Google), ce surpoids le compense optiquement.
   - L'écart entre les entailles vaut 11,7. Il reste volontairement INFÉRIEUR à
     l'épaisseur (14,2) : tant que le vide est plus mince que le plein, l'œil
     regroupe les trois entailles en une seule forme. À écart égal ou supérieur
     — la proportion d'Adidas — elles se détachent en trois objets distincts.
     Adidas peut se le permettre : ses bandes sont reproduites immenses et sans
     logotype à côté. Pas le cas ici.

   ── Graisse : Albert Sans 500, tracking +0.05em ───────────────────────────
   Le logotype était en 600 très serré (-0.03em). Le 500 espacé respire, en
   accord avec la charte (« beaucoup d'espace »), sans basculer dans le code
   TYPOGRAPHIQUE DE LA MODE — capitales fines très écartées — qui contredirait
   « Des systèmes. Pas des heures. ». C'est pour cela que le tracking s'arrête
   à +0.05em : au-delà, le mot cesse d'être un logotype tech.

   Le A étant DESSINÉ et non typographié, il a fallu le retracer pour cette
   graisse : sa jambe passe de 15 à 12,5 d'épaisseur horizontale. Le symbole a
   suivi. Changer la graisse du mot impose donc de refaire ces trois pièces —
   ce n'est jamais un simple changement de `font-weight`.

   Le viewBox ne déborde plus : le bas des entailles est à y=100, soit pile la
   ligne de base. Le lockup n'a donc plus besoin du margin-bottom négatif qui
   corrigeait l'ancien débord.

   La police du logotype est Albert Sans 500, déjà chargée par public/index.html.
   Elle est imposée en dur ici : le logo ne doit pas changer de forme si la
   typographie du site évolue.
   ========================================================================== */

const INK = "#1d1d1f";

/* Pente du A : 39.5 / 100. Toute entaille montant de `h` se décale de 0.395*h. */
const MARK_VIEWBOX = "0 0 105.5 100";
const MARK_SLASHES = [
  "M0 100 H14.2 L44.22 24 H30.02 Z",
  "M25.9 100 H40.1 L74.86 12 H60.66 Z",
  "M51.8 100 H66 L105.5 0 H91.3 Z",
];
/* A ouvert, graisse 500 : jambe de 12,5 d'épaisseur horizontale (15 en 600). */
const A_VIEWBOX = "0 0 91.5 100";
const A_PATH = "M0 100 L39.5 0 L52 0 L91.5 100 L79 100 L45.75 15.82 L12.5 100 Z";

const WORDMARK_FONT = "'Albert Sans', system-ui, sans-serif";

/* Ratio de la boîte du symbole : 105.5 / 100. */
const MARK_RATIO = 105.5 / 100;

/* ── Le A ouvert ───────────────────────────────────────────────
   Dimensionné en em pour suivre la taille du texte qui l'entoure.
   0.72em = hauteur de capitale d'Albert Sans 500 ; aligné par la ligne de
   base, son bord bas tombe donc pile sur la baseline, sans correction. */
function OpenA() {
  return (
    <svg
      viewBox={A_VIEWBOX}
      aria-hidden="true"
      focusable="false"
      style={{ width: "0.6325em", height: "0.72em", margin: "0 0.005em 0 -0.012em" }}
    >
      <path d={A_PATH} fill="currentColor" />
    </svg>
  );
}

/* ── Symbole seul ──────────────────────────────────────────────
   `size` = hauteur en px. La largeur en découle, jamais l'inverse.
   `animate` joue l'ouverture : les trois entailles entrent décalées, montent
   en place et s'arrêtent net. Aucun rebond, et rien du tout si l'utilisateur
   a demandé à réduire les animations. */
export function TangigMark({
  size = 24,
  color = "currentColor",
  animate = false,
  title = "TANGIG",
  style,
  ...rest
}) {
  const decorative = title === null;
  return (
    <svg
      viewBox={MARK_VIEWBOX}
      width={size * MARK_RATIO}
      height={size}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : title}
      aria-hidden={decorative ? "true" : undefined}
      focusable="false"
      className={animate ? "tangig-mark-intro" : undefined}
      style={{ display: "block", ...style }}
      {...rest}
    >
      {MARK_SLASHES.map((d, i) => (
        <g key={d} style={animate ? { animationDelay: `${i * 130}ms` } : undefined}>
          <path d={d} fill={color} />
        </g>
      ))}
    </svg>
  );
}

/* ── Le mot seul ───────────────────────────────────────────────
   `size` = font-size en px (ou toute unité CSS). */
export function TangigWordmark({ size = 22, color = "currentColor", style, ...rest }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "baseline",
        fontFamily: WORDMARK_FONT,
        fontWeight: 500,
        letterSpacing: "0.05em",
        lineHeight: 1,
        fontSize: typeof size === "number" ? `${size}px` : size,
        color,
        whiteSpace: "nowrap",
        ...style,
      }}
      {...rest}
    >
      {/* Le mot est découpé pour intercaler le A dessiné. Le lecteur d'écran
          n'entend qu'une seule fois « TANGIG » grâce à l'aria-label du parent. */}
      <span aria-hidden="true">T</span>
      <OpenA />
      <span aria-hidden="true">NGIG</span>
    </span>
  );
}

/* ── Lockup horizontal ─────────────────────────────────────────
   L'assemblage officiel : header, e-mails, factures, signatures.
   `size` = font-size du mot ; le symbole s'y accorde tout seul. */
export function TangigLockup({ size = 22, color = "currentColor", style, ...rest }) {
  return (
    <span
      role="img"
      aria-label="TANGIG"
      style={{
        display: "inline-flex",
        alignItems: "baseline",
        fontFamily: WORDMARK_FONT,
        fontWeight: 500,
        letterSpacing: "0.05em",
        lineHeight: 1,
        fontSize: typeof size === "number" ? `${size}px` : size,
        color,
        whiteSpace: "nowrap",
        ...style,
      }}
      {...rest}
    >
      {/* Hauteur = 0.72em, la capitale d'Albert Sans 500, comme le A dessiné :
          le bas des entailles est à y=100 dans le viewBox, donc l'alignement
          par la ligne de base suffit — aucune correction verticale. */}
      <svg
        viewBox={MARK_VIEWBOX}
        aria-hidden="true"
        focusable="false"
        style={{ width: `${(0.72 * MARK_RATIO).toFixed(4)}em`, height: "0.72em", margin: "0 0.16em 0 0" }}
      >
        {MARK_SLASHES.map((d) => (
          <path key={d} d={d} fill="currentColor" />
        ))}
      </svg>
      <span aria-hidden="true">T</span>
      <OpenA />
      <span aria-hidden="true">NGIG</span>
    </span>
  );
}

export { INK as TANGIG_INK };
export default TangigLockup;
